import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface FoodRule {
  name: string;
  nameEn: string;
  icon: string;
  keyPoints: { point: string; detail: string }[];
  color: string;
}

const RULES: FoodRule[] = [
  {
    name: '温度控制',
    nameEn: 'Temperature Control',
    icon: '🌡️',
    keyPoints: [
      { point: '危险温区 5-60°C', detail: '细菌在此区间每20分钟翻倍繁殖' },
      { point: '冷藏 ≤ 4°C', detail: '生鲜食品需在2小时内放入冰箱' },
      { point: '加热 ≥ 70°C', detail: '食物中心温度达70°C可杀灭多数细菌' },
    ],
    color: '#FF4757',
  },
  {
    name: '交叉污染防控',
    nameEn: 'Cross-Contamination',
    icon: '🧤',
    keyPoints: [
      { point: '生熟分开', detail: '使用不同砧板和刀具处理生熟食材' },
      { point: '上熟下生', detail: '冰箱中熟食放上层，生食放下层' },
      { point: '手部清洁', detail: '接触生肉后必须用肥皂洗手20秒' },
    ],
    color: '#FFD700',
  },
  {
    name: '保质期管理',
    nameEn: 'Shelf Life',
    icon: '📅',
    keyPoints: [
      { point: '先进先出', detail: '新购食品放后面，先用旧的' },
      { point: '开封后缩短', detail: '牛奶开封后冷藏不超过3天' },
      { point: '冷冻保存', detail: '肉类冷冻可延长至3-6个月' },
    ],
    color: '#4ECDC4',
  },
  {
    name: '食物中毒预防',
    nameEn: 'Food Poisoning',
    icon: '⚠️',
    keyPoints: [
      { point: '沙门氏菌', detail: '彻底煮熟蛋类和禽肉可有效预防' },
      { point: '黄曲霉毒素', detail: '丢弃发霉花生和谷物，不可切除食用' },
      { point: '亚硝酸盐', detail: '隔夜绿叶蔬菜亚硝酸盐显著升高' },
    ],
    color: '#667EEA',
  },
];

const TemperatureBar: React.FC<{ frame: number; phaseStart: number }> = ({ frame, phaseStart }) => {
  const barProg = interpolate(frame, [phaseStart + 10, phaseStart + 40], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const zones = [
    { label: '冷冻 -18°C', start: 0, end: 10, color: '#3498DB' },
    { label: '冷藏 0-4°C', start: 10, end: 18, color: '#4ECDC4' },
    { label: '⚠ 危险区 5-60°C', start: 18, end: 68, color: '#FF4757' },
    { label: '安全 ≥70°C', start: 68, end: 100, color: '#2ECC71' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, opacity: barProg, margin: '16px 0' }}>
      <div style={{ display: 'flex', height: 50, borderRadius: 20, overflow: 'hidden' }}>
        {zones.map((z) => (
          <div
            key={z.label}
            style={{
              width: `${(z.end - z.start) * barProg}%`,
              background: z.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 26, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {z.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FoodSafetyAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentIdx = Math.min(
    Math.floor(Math.max(0, frame - 50) / 120),
    RULES.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: '#0B1120',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FF4757, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          食品安全必知
        </h1>
        <p style={{ fontSize: 26, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Food Safety · 守护舌尖上的安全
        </p>
      </div>

      {/* Category tabs */}
      <div
        style={{
          position: 'absolute',
          top: 280,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 12,
        }}
      >
        {RULES.map((rule, i) => {
          const tabProg = spring({
            frame: frame - 10 - i * 6,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const isActive = i === currentIdx;

          return (
            <div
              key={rule.nameEn}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: isActive ? `${rule.color}15` : 'rgba(255,255,255,0.03)',
                border: `2px solid ${isActive ? rule.color + '50' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 14,
                textAlign: 'center',
                opacity: tabProg,
              }}
            >
              <span style={{ fontSize: 36 }}>{rule.icon}</span>
              <div
                style={{
                  fontSize: 30,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? rule.color : 'rgba(255,255,255,0.35)',
                  marginTop: 4,
                }}
              >
                {rule.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail cards */}
      {RULES.map((rule, i) => {
        const phaseStart = 50 + i * 120;
        const phaseEnd = phaseStart + 115;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 18], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === RULES.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={rule.nameEn}
            style={{
              position: 'absolute',
              top: 480,
              left: 55,
              right: 55,
              opacity,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
              <span style={{ fontSize: 48 }}>{rule.icon}</span>
              <div>
                <h2 style={{ fontSize: 42, fontWeight: 700, color: rule.color, margin: 0 }}>
                  {rule.name}
                </h2>
                <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)' }}>{rule.nameEn}</span>
              </div>
            </div>

            {i === 0 && <TemperatureBar frame={frame} phaseStart={phaseStart} />}

            <div style={{ display: 'flex', gap: 20, marginTop: 20 }}>
              {rule.keyPoints.map((kp, ki) => {
                const kpProg = spring({
                  frame: frame - phaseStart - 15 - ki * 10,
                  fps,
                  config: { damping: 14, stiffness: 120 },
                });

                return (
                  <div
                    key={kp.point}
                    style={{
                      flex: 1,
                      padding: '28px 30px',
                      background: `${rule.color}08`,
                      border: `2px solid ${rule.color}25`,
                      borderRadius: 20,
                      opacity: kpProg,
                      transform: `translateY(${interpolate(kpProg, [0, 1], [20, 0])}px)`,
                    }}
                  >
                    <h4 style={{ fontSize: 28, fontWeight: 700, color: rule.color, margin: '0 0 12px' }}>
                      {kp.point}
                    </h4>
                    <p style={{ fontSize: 26, color: '#D1D5DB', lineHeight: 1.6, margin: 0 }}>
                      {kp.detail}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        {RULES.map((r, i) => (
          <div
            key={r.nameEn}
            style={{
              width: i === currentIdx ? 44 : 12,
              height: 12,
              borderRadius: 6,
              background: i === currentIdx ? r.color : 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
